import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { parseGooglePlace } from "../screens/host-experience/host-add-space/components/GooglePlacesSearchInput";
import logger from "../utils/logger";

/**
 * Clean Google Places Location Autocomplete input for search card forms.
 * Maintains custom landing design while integrating Google Maps Places Autocomplete API.
 */
export default function LocationSearchInput({
  value = "",
  onChange = _val => {},
  onPlaceSelected = _place => {},
  placeholder = "Search location...",
  className = "",
  inputClassName = "",
  disabled = false,
  ariaLabel = "",
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsReady(true);
    } else {
      const interval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setIsReady(true);
          clearInterval(interval);
        }
      }, 300);
      return () => clearInterval(interval);
    }
  }, []);

  useEffect(() => {
    if (!isReady || !inputRef.current || disabled) return;

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        fields: ["address_components", "geometry", "name", "formatted_address"],
        types: ["geocode", "establishment"],
      });
      autocompleteRef.current = autocomplete;

      const handlePlaceChanged = () => {
        const place = autocomplete.getPlace();
        if (!place?.geometry?.location) return;
        const parsed = parseGooglePlace(place);
        const locationText = parsed.city || parsed.streetAddress || parsed.formattedAddress || place.name || "";
        if (onChange) onChange(locationText);
        if (onPlaceSelected) onPlaceSelected(parsed);
      };

      autocomplete.addListener("place_changed", handlePlaceChanged);
    } catch (e) {
      logger.warn("LocationSearchInput: Error initializing google places autocomplete", e);
    }

    return () => {
      if (window.google?.maps?.event && autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isReady, disabled, onChange, onPlaceSelected]);

  /* Interceptor for Google Places dropdown z-index & clicks */
  useEffect(() => {
    const handlePacClick = e => {
      if (e.target.closest(".pac-container")) {
        e.stopPropagation();
      }
    };
    document.addEventListener("mousedown", handlePacClick, true);
    document.addEventListener("pointerdown", handlePacClick, true);
    document.addEventListener("click", handlePacClick, true);

    return () => {
      document.removeEventListener("mousedown", handlePacClick, true);
      document.removeEventListener("pointerdown", handlePacClick, true);
      document.removeEventListener("click", handlePacClick, true);
    };
  }, []);

  return (
    <div className={`flex items-center bg-gray-50 rounded-xl px-3.5 h-11 border border-gray-200 relative ${className}`}>
      <style>{`
        .pac-container {
          z-index: 99999 !important;
          pointer-events: auto !important;
          border-radius: 0.75rem !important;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05) !important;
          border: 1px solid #e5e7eb !important;
          background-color: #ffffff !important;
          margin-top: 6px !important;
          padding: 4px !important;
          font-family: inherit !important;
        }
        .pac-item {
          padding: 8px 12px !important;
          cursor: pointer !important;
          font-size: 12px !important;
          line-height: 1.4 !important;
          border-top: none !important;
          border-radius: 0.5rem !important;
          color: #1f2937 !important;
          transition: background-color 0.15s ease, color 0.15s ease !important;
          display: flex !important;
          items-center: center !important;
        }
        .pac-item:hover, .pac-item-selected {
          background-color: #eff6ff !important;
          color: #1d4ed8 !important;
        }
        .pac-icon {
          display: none !important;
        }
        .pac-item-query {
          font-size: 12px !important;
          color: #111827 !important;
          padding-right: 4px !important;
        }
        .pac-matched {
          font-weight: 700 !important;
          color: #2563eb !important;
        }
        .hdpi .pac-logo:after {
          background-image: none !important;
          height: 0 !important;
        }
        .pac-container:after {
          display: none !important;
        }
      `}</style>
      <MapPin className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange && onChange(e.target.value)}
        disabled={disabled}
        aria-label={ariaLabel || placeholder}
        autoComplete="off"
        className={`bg-transparent w-full text-xs font-medium text-gray-800 focus:outline-none disabled:cursor-not-allowed ${inputClassName}`}
      />
    </div>
  );
}
